import {DependencyInjectId} from '@@/package/const/DependencyInjectId';
import HttpClient from '@@/package/network/HttpClient';
import {seatHeader} from '~/composables/useAssessmentSeat';
import {sampleAccessHeader, storeSampleAccessTicket} from '~/composables/useSampleAccessTicket';
import {type ApplicationContextInterface} from '@@/package/application/applicationContext';
import {type AxiosResponse} from 'axios';
import type {PageModel} from '~~/package/mapper/common/model/PageModel';
import {ProSkillAssureTrackModel} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureTrackModel';
import type ProSkillAssureAssessmentSetResponse
  from '~~/package/apis/ProSkillAssure/response/ProSkillAssureAssessmentSetResponse';
import {
  ProSkillAssureAnswerCodeModel
} from '~~/package/mapper/proSkillAssure/model/assessment/ProSkillAssureAnswerCodeModel';
import {
  ProSkillAssureAssessmentMyInfoModel
} from '~~/package/mapper/proSkillAssure/model/assessment/myInfo/ProSkillAssureAssessmentMyInfoModel';
import {ProSkillAssureMyAssessmentModel} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureMyAssessmentModel';
import type {
  ProSkillAssureAssessmentInfoModel
} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureAssessmentInfoModel';
import type {
  ProSkillAssureAssessmentDataModel
} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureAssessmentDataModel';
import type ProSkillAssureCreateRequest from '~~/package/apis/ProSkillAssure/request/ProSkillAssureCreateRequest';
import type ProSkillAssureAssessmentDetailResponse
  from '~~/package/apis/ProSkillAssure/response/ProSkillAssureAssessmentDetailResponse';
import {LevelAssessmentResultModel} from '~~/package/mapper/levelAssessment/model/LevelAssessmentResultModel';
import type ProSkillAssureTaskResponse from '~~/package/apis/ProSkillAssure/response/ProSkillAssureTaskResponse';
import {CautionType} from '~~/package/types/CautionType';
import type ProSkillAssureTimeZoneResponse
  from '~~/package/apis/ProSkillAssure/response/ProSkillAssureTimeZoneResponse';
import type CustomResponse from '~~/package/types/ResponseType';
import type {LearningTokenData} from '~~/package/mapper/learning/model/LearningTokenData';
import {
  ProSkillAssureTemplateCreateModel
} from '~~/package/mapper/proSkillAssure/model/template/ProSkillAssureTemplateCreateModel';
import type {
  ProSkillAssureTemplateModel
} from '~~/package/mapper/proSkillAssure/model/template/ProSkillAssureTemplateModel';
import type {
  ProSkillAssureSampleAssessmentModel
} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureSampleAssessmentModel';
import type ProSkillAssureCandidateValidateResponse
  from '~~/package/apis/ProSkillAssure/response/ProSkillAssureCandidateValidateResponse';
import type ProSkillAssureEnvironmentReportRequest
  from '~~/package/apis/ProSkillAssure/request/ProSkillAssureEnvironmentReportRequest';
import type ProSkillAssureAiRecommendEntryResponse
  from '~~/package/apis/ProSkillAssure/response/ProSkillAssureAiRecommendEntryResponse';
import type ProSkillAssureAiRecommendResponse
  from '~~/package/apis/ProSkillAssure/response/ProSkillAssureAiRecommendResponse';
import type ProSkillAssureAiRecommendRequest
  from '~~/package/apis/ProSkillAssure/request/ProSkillAssureAiRecommendRequest';

export default class ProSkillAssureApi {

  protected http: HttpClient;

  constructor(context: ApplicationContextInterface) {
    this.http = context.getService<HttpClient>(DependencyInjectId.HttpClient);
  }

  public getProSkillAssessmentTrack(page: number, itemSize: number, categoryList?: string[], fields?: string, type?: string[], trackTitle?: string): Promise<AxiosResponse<CustomResponse<PageModel<ProSkillAssureTrackModel>>>> {
    return this.http.get<AxiosResponse<CustomResponse<PageModel<ProSkillAssureTrackModel>>>>("/v2/assessments/sets", { page, itemSize, categoryList, fields, type, trackTitle }, true);
  }

  public getProSkillAssureAssessmentData(assessmentId: number): Promise<AxiosResponse<CustomResponse<ProSkillAssureAssessmentSetResponse>>> {
    return this.http.get<AxiosResponse<CustomResponse<ProSkillAssureAssessmentSetResponse>>>(`/v2/assessments/${assessmentId}/sets`, {}, true);
  }

  public getAssessmentRemainingTime(assessmentId: number): Promise<AxiosResponse<CustomResponse<number>>> {
    return this.http.get<AxiosResponse<CustomResponse<number>>>(`/v2/assessments/${assessmentId}/remaining-time`, {}, true);
  }

  /**
   * 설계 §4a: 샘플 평가에서 티켓을 요구하는 요청은 정확한 문항 조회 GET
   * `/v2/assessments/{id}/sets/{templateId}/{taskId}` 하나뿐이다. 같은 경로의 POST
   * (문항 제출), `/sets` 목록·`remaining-time` 은 요구하지 않는다. 비샘플·티켓
   * 미보유 시 빈 헤더가 나간다.
   */
  public getProSkillAssureAssessmentTask(assessmentId: number, testTemplateId: number, taskId: number): Promise<AxiosResponse<CustomResponse<ProSkillAssureTaskResponse>>> {
    return this.http.get<AxiosResponse<CustomResponse<ProSkillAssureTaskResponse>>>(`/v2/assessments/${assessmentId}/sets/${testTemplateId}/${taskId}`, {}, true, null, false, true, sampleAccessHeader(assessmentId));
  }

  /**
   * Re-mints the embed URL (fresh B-handle) for a task WITHOUT reloading the task data. Called when
   * (re)opening an agent-prompt lab: the cached task.embedUrl may hold an expired handle, which the
   * proxy rejects (401) so the USD budget can't apply. Returns the URL string only.
   */
  public getProSkillAssureEmbedUrl(assessmentId: number, testTemplateId: number, taskId: number): Promise<AxiosResponse<CustomResponse<string>>> {
    return this.http.get<AxiosResponse<CustomResponse<string>>>(`/v2/assessments/${assessmentId}/sets/${testTemplateId}/${taskId}/embed-url`, {}, true, null, false, true, sampleAccessHeader(assessmentId));
  }

  // ASMT-CAND-13: 변조 방지 제출 — v2.2 경로 + HMAC(hmac/issued_at 헤더), 답안은 Base64 인코딩
  public requestAnswer(assessmentId: number, testTemplateId: number, taskId: number, answer: ProSkillAssureAnswerCodeModel, tokenData: LearningTokenData): Promise<AxiosResponse<CustomResponse<boolean>>> {
    return this.http.post<AxiosResponse<CustomResponse<boolean>>, ProSkillAssureAnswerCodeModel>(
        `/v2.2/assessments/${assessmentId}/sets/${testTemplateId}/${taskId}`,
        answer,
        true,
        null,
        false,
        // TSK-11215: saving an answer is a write, so it must prove this screen still holds
        // the seat. A displaced screen gets 409 CP_1015 instead of silently overwriting.
        {hmac: tokenData.hmac, issued_at: tokenData.issuedAt, ...seatHeader()}
    );
  }

  public updateRulesAgreeFlag(assessmentId: number, rulesAgreeFlag: boolean): Promise<AxiosResponse<CustomResponse<boolean>>> {
    return this.http.patch<AxiosResponse<CustomResponse<boolean>>, {}>(`/v2/assessments/${assessmentId}/rules-agree?flag=${rulesAgreeFlag}`, {}, true)
  }

  /**
   * TSK-11215: `START` is the seat-issuing call (the response body carries the new seat id);
   * `COMPLETED` is a write, so it carries the current seat back for validation.
   */
  public updateStatus(assessmentId: number, status: "COMPLETED" | "START"): Promise<AxiosResponse<CustomResponse<string>>> {
    return this.http.put<AxiosResponse<CustomResponse<string>>, {}>(
        `/v2/assessments/${assessmentId}/status?status=${status}`,
        {},
        true,
        false,
        status === "COMPLETED" ? seatHeader() : {}
    ).then((response) => {
      // The sample ticket starts at the actual taking-screen START request, not at my/info.
      if (status === "START") {
        storeSampleAccessTicket(assessmentId, response.headers as Record<string, unknown>);
      }
      return response;
    });
  }

  /**
   * 설계 §4a: my/info는 sampleAvailable만 제공한다. 샘플 접근 티켓은 실제 START 응답에서
   * 발급되어야 하므로, 이 응답에서는 티켓 저장을 시도하지 않는다.
   */
  public getProSkillAssureMyInfo(assessmentId: number): Promise<AxiosResponse<CustomResponse<ProSkillAssureAssessmentMyInfoModel>>> {
    return this.http.get<AxiosResponse<CustomResponse<ProSkillAssureAssessmentMyInfoModel>>>(`/v2/assessments/${assessmentId}/my/info`, {}, true)
  }

  public getProSkillAssureAssessmentList(page: number, itemSize: number, filters?: string[], assessmentTitle?: string): Promise<AxiosResponse<CustomResponse<PageModel<ProSkillAssureAssessmentDataModel>>>> {
      return this.http.get<AxiosResponse<CustomResponse<PageModel<ProSkillAssureAssessmentDataModel>>>>("/v2/assessments", { page, itemSize, filters, assessmentTitle, assessmentType: "" }, true);
  }

  public getMyAssessmentList(page?: number, size?: number, status?: string[], isAdmin: boolean = false): Promise<AxiosResponse<CustomResponse<PageModel<ProSkillAssureMyAssessmentModel>>>> {
    return this.http.get<AxiosResponse<CustomResponse<PageModel<ProSkillAssureMyAssessmentModel>>>>(isAdmin ? "/v2/assessments/my/org" : "/v2/assessments/my", { page, size, status }, true);
  }

  public getSampleAssessmentList(lang: string, page: number, itemSize: number): Promise<AxiosResponse<CustomResponse<PageModel<ProSkillAssureSampleAssessmentModel>>>> {
    return this.http.get<AxiosResponse<CustomResponse<PageModel<ProSkillAssureSampleAssessmentModel>>>>('/v2.2/assessments/samples', { page,  itemSize }, true, lang);
  }

  public getProSkillAssureAssessmentInfo(customAssessmentId: number): Promise<AxiosResponse<CustomResponse<ProSkillAssureAssessmentInfoModel>>> {
    return this.http.get<AxiosResponse<CustomResponse<ProSkillAssureAssessmentInfoModel>>>(`/v2/assessments/${customAssessmentId}/info`, undefined, true);
  }

  public getProSkillAssureAssessmentDetail(customAssessmentId: number): Promise<AxiosResponse<CustomResponse<ProSkillAssureAssessmentDetailResponse>>> {
    return this.http.get<AxiosResponse<CustomResponse<ProSkillAssureAssessmentDetailResponse>>>(`/v2/assessments/${customAssessmentId}/detail`, undefined, true);
  }

  public deleteProSkillAssureCandidates(customAssessmentId: number, userIds: number[]): Promise<AxiosResponse<CustomResponse<boolean>>> {
    return this.http.delete<AxiosResponse<CustomResponse<boolean>>>(`/v2/assessments/${customAssessmentId}/candidates?${userIds.map(id => `userIds=${id}`).join("&")}`, undefined, true);
  }

  public setProSkillAssureAssessment(assessmentSets: ProSkillAssureCreateRequest): Promise<AxiosResponse<CustomResponse<number>>> {
    return this.http.post("/v2/custom-assessments", assessmentSets, true);
  }

  public addProSkillAssureCandidateToAssessment(emailList: string[], assessmentId: number): Promise<AxiosResponse<CustomResponse<boolean>>> {
    return this.http.post(`/v2/assessments/${assessmentId}/candidates`, { emailList }, true);
  }

  public validateProSkillAssureCandidates(assessmentId: number, emailList: string[]): Promise<AxiosResponse<CustomResponse<ProSkillAssureCandidateValidateResponse>>> {
    return this.http.post(`/v2/assessments/${assessmentId}/candidates/validate`, { emailList }, true);
  }

  public updateProSkillAssureAssessment(customAssessmentId: number, assessment: ProSkillAssureCreateRequest): Promise<AxiosResponse<CustomResponse<boolean>>> {
    return this.http.put(`/v2/custom-assessments/${customAssessmentId}`, assessment, true);
  }

  public deleteProSkillAssureAssessment(customAssessmentId: number): Promise<AxiosResponse<CustomResponse<boolean>>> {
    return this.http.delete<AxiosResponse<CustomResponse<boolean>>>(`/v2/assessments/${customAssessmentId}`, undefined, true);
  }

  public getProSkillAssureAssessmentSampleList(testTemplateId: number): Promise<AxiosResponse<CustomResponse<LevelAssessmentResultModel[]>>> {
    return this.http.get<AxiosResponse<CustomResponse<LevelAssessmentResultModel[]>>>(`/v2/assessments/sets/template/${testTemplateId}/task/list`, undefined, true);
  }

  public addProSkillAssureAssessmentSaveCandidateAction(customAssessmentId: number, suspiciousAction: CautionType, actionContent?: string): Promise<AxiosResponse<CustomResponse<number>>> {
    return this.http.post(`/v2/assessments/${customAssessmentId}/candidates/my/actions`, { suspiciousAction, actionContent }, true);
  }

  /** ASMT-PROCTOR-16 (TSK-11692): 응시 환경 진입 차단 / 점검 실패를 운영팀 웹훅으로 알린다. */
  public reportEnvironmentEvent(customAssessmentId: number, request: ProSkillAssureEnvironmentReportRequest): Promise<AxiosResponse<void>> {
    return this.http.post(`/v2/assessments/${customAssessmentId}/environment-report`, request, true);
  }

  public getTimezoneList(): Promise<AxiosResponse<CustomResponse<ProSkillAssureTimeZoneResponse[]>>> {
    return this.http.get<AxiosResponse<CustomResponse<ProSkillAssureTimeZoneResponse[]>>>(`/countryRegion/timeZone/list`, undefined, true);
  }

  public getProSkillAssureTemplate(page: number, itemSize: number, lang: string, title?: string): Promise<AxiosResponse<CustomResponse<PageModel<ProSkillAssureTemplateModel>>>> {
    return this.http.get<AxiosResponse<CustomResponse<PageModel<ProSkillAssureTemplateModel>>>>(`/v2/assessments/templates`, { page, itemSize, LANG: lang, title }, true);
  }

  public addProSkillAssureTemplate(request: ProSkillAssureTemplateCreateModel): Promise<AxiosResponse<CustomResponse<boolean>>> {
    return this.http.post(`/v2/assessments/templates`, request, true);
  }

  public modifyProSkillAssureTemplate(title: string, assessmentTemplateId: number): Promise<AxiosResponse<CustomResponse<boolean>>> {
    return this.http.patch<AxiosResponse<CustomResponse<boolean>>, {}>(`/v2/assessments/templates/${assessmentTemplateId}`, { title }, true);
  }

  public removeProSkillAssureTemplate(assessmentTemplateId: number): Promise<AxiosResponse<CustomResponse<boolean>>> {
    return this.http.delete<AxiosResponse<CustomResponse<boolean>>>(`/v2/assessments/templates/${assessmentTemplateId}`, undefined, true);
  }

  // ASMT-AUTH-13: Step1 트랙 추천받기 — 진입 정보 조회(GET)·추천 요청(POST) /v2/custom-assessments/recommendations
  public getAiRecommendEntry(): Promise<AxiosResponse<CustomResponse<ProSkillAssureAiRecommendEntryResponse>>> {
    return this.http.get<AxiosResponse<CustomResponse<ProSkillAssureAiRecommendEntryResponse>>>('/v2/custom-assessments/recommendations', undefined, true);
  }

  public requestAiRecommend(request: ProSkillAssureAiRecommendRequest): Promise<AxiosResponse<CustomResponse<ProSkillAssureAiRecommendResponse>>> {
    return this.http.post<AxiosResponse<CustomResponse<ProSkillAssureAiRecommendResponse>>, ProSkillAssureAiRecommendRequest>('/v2/custom-assessments/recommendations', request, true);
  }

}
